import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const Navbar: React.FC = () => {
    const [scrolled, setScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('');
    const [isHeroSection, setIsHeroSection] = useState(true);

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            setScrolled(scrollPosition > 50);
            setIsHeroSection(scrollPosition < 100);

            // Determine which section is currently in view
            const sections = ['hero', 'portfolio', 'watchlist', 'signals', 'ai-assistant', 'market'];
            let currentSection = '';

            for (const section of sections) {
                const element = document.getElementById(section);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    if (rect.top <= 100 && rect.bottom >= 100) {
                        currentSection = section;
                        break;
                    }
                }
            }

            setActiveSection(currentSection);
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const navLinks = [
        { name: 'Portfolio', href: '#portfolio' },
        { name: 'Watchlist', href: '#watchlist' },
        { name: 'AI Assistant', href: '#ai-assistant' },
        { name: 'Market', href: '#market' },
        { name: 'Subscription', href: '#subscription' },
    ];

    const linkVariants = {
        hover: {
            y: -2,
            transition: { duration: 0.2 }
        },
        tap: {
            y: 0,
            scale: 0.95,
            transition: { duration: 0.1 }
        }
    };

    // Check if a nav link is active based on current section
    const isActive = (href: string) => {
        const sectionId = href.replace('#', '');
        return activeSection === sectionId;
    };

    return (
        <motion.nav
            className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled
                ? 'py-3 bg-black/80 backdrop-blur-sm'
                : 'py-5 bg-transparent'
                }`}
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.6, 0.05, 0.01, 0.9] }}
        >
            <div className="container-custom flex justify-between items-center">
                <motion.div
                    className="flex items-center"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    style={{ cursor: 'pointer' }}
                >
                    <span className={`text-2xl font-bold ${isHeroSection ? 'text-gray-800' : 'text-white'}`}>
                        Haru<span className="text-accent">Kuro</span>
                    </span>
                </motion.div>

                {/* Desktop Navigation */}
                <div className="hidden md:flex space-x-8">
                    {navLinks.map((link) => (
                        <motion.a
                            key={link.name}
                            href={link.href}
                            className={`font-medium relative ${isActive(link.href)
                                ? 'text-blue-400'
                                : isHeroSection
                                    ? 'text-gray-800'
                                    : 'text-white'
                                } hover:text-blue-400 transition-colors duration-200`}
                            variants={linkVariants}
                            whileHover="hover"
                            whileTap="tap"
                        >
                            {link.name}
                            <motion.span
                                className="absolute bottom-0 left-0 h-[2px] bg-blue-400"
                                initial={{ width: '0%' }}
                                animate={{ width: isActive(link.href) ? '100%' : '0%' }}
                                transition={{ duration: 0.3 }}
                            />
                        </motion.a>
                    ))}
                </div>

                {/* Mobile menu button */}
                <motion.button
                    className={`md:hidden p-2 ${isHeroSection ? 'text-gray-800' : 'text-white'}`}
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {isMenuOpen ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                        )}
                    </svg>
                </motion.button>

                {/* Mobile Navigation */}
                {isMenuOpen && (
                    <motion.div
                        className="absolute top-full left-0 w-full bg-black/90 md:hidden p-4 rounded-b-xl shadow-xl"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                    >
                        <div className="flex flex-col space-y-4">
                            {navLinks.map((link) => (
                                <motion.a
                                    key={link.name}
                                    href={link.href}
                                    className={`font-medium py-2 px-4 rounded ${isActive(link.href) ? 'bg-blue-400/20 text-blue-400' : 'text-white hover:bg-gray-800'}`}
                                    variants={linkVariants}
                                    whileHover="hover"
                                    whileTap="tap"
                                >
                                    {link.name}
                                </motion.a>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* CTA Button */}
                <motion.a
                    href="https://levi.harukurosystems.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden md:block px-5 py-2 bg-blue-400 rounded-md text-white font-medium"
                    whileHover={{
                        scale: 1.05,
                        boxShadow: '0 0 15px rgba(96, 165, 250, 0.5)'
                    }}
                    whileTap={{ scale: 0.95 }}
                >
                    Get Started
                </motion.a>
            </div>
        </motion.nav>
    );
};

export default Navbar; 